import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

// Telegram WebApp types
interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
}

interface ThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
}

interface WebAppInitData {
  query_id?: string;
  user?: TelegramUser;
  auth_date?: number;
  hash?: string;
}

interface TelegramWebApp {
  initData: string;
  initDataUnsafe: WebAppInitData;
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: ThemeParams;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  headerColor: string;
  backgroundColor: string;
  isClosingConfirmationEnabled: boolean;
  BackButton: {
    isVisible: boolean;
    onClick: (callback: () => void) => void;
    offClick: (callback: () => void) => void;
    show: () => void;
    hide: () => void;
  };
  MainButton: {
    text: string;
    color: string;
    textColor: string;
    isVisible: boolean;
    isActive: boolean;
    isProgressVisible: boolean;
    setText: (text: string) => void;
    onClick: (callback: () => void) => void;
    offClick: (callback: () => void) => void;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    showProgress: (leaveActive?: boolean) => void;
    hideProgress: () => void;
  };
  HapticFeedback: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
  ready: () => void;
  expand: () => void;
  close: () => void;
  sendData: (data: string) => void;
  openLink: (url: string, options?: { try_instant_view?: boolean }) => void;
  openTelegramLink: (url: string) => void;
  showAlert: (message: string, callback?: () => void) => void;
  showConfirm: (message: string, callback?: (confirmed: boolean) => void) => void;
  showPopup: (params: {
    title?: string;
    message: string;
    buttons?: Array<{
      id?: string;
      type?: 'default' | 'ok' | 'close' | 'cancel' | 'destructive';
      text?: string;
    }>;
  }, callback?: (buttonId: string) => void) => void;
  setHeaderColor: (color: string) => void;
  setBackgroundColor: (color: string) => void;
  enableClosingConfirmation: () => void;
  disableClosingConfirmation: () => void;
}

declare global {
  interface Window {
    Telegram?: {
      WebApp: TelegramWebApp;
    };
  }
}

interface TelegramContextType {
  webApp: TelegramWebApp | null;
  user: TelegramUser | null;
  isInTelegram: boolean;
  isMiniApp: boolean;
  colorScheme: 'light' | 'dark';
  themeParams: ThemeParams;
  ready: () => void;
  expand: () => void;
  close: () => void;
  showAlert: (message: string) => Promise<void>;
  showConfirm: (message: string) => Promise<boolean>;
  hapticFeedback: (type: 'success' | 'warning' | 'error' | 'light' | 'medium' | 'heavy') => void;
  openLink: (url: string) => void;
  setMainButton: (text: string, onClick: () => void) => void;
  hideMainButton: () => void;
  showBackButton: (onClick: () => void) => void;
  hideBackButton: () => void;
}

const TelegramContext = createContext<TelegramContextType | null>(null);

// Helper to compare Telegram WebApp versions
function isVersionAtLeast(version: string, minVersion: string): boolean {
  const current = version.split('.').map(Number);
  const required = minVersion.split('.').map(Number);

  for (let i = 0; i < Math.max(current.length, required.length); i++) {
    const c = current[i] || 0;
    const r = required[i] || 0;
    if (c > r) return true;
    if (c < r) return false;
  }
  return true;
}

export function TelegramProvider({ children }: { children: ReactNode }) {
  const [webApp, setWebApp] = useState<TelegramWebApp | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Only attempt to load the Telegram SDK when actually opened inside the
    // Telegram in-app browser. On the public web (po.enana.me) the request to
    // telegram.org times out and slows page load.
    const inTelegram =
      typeof window !== 'undefined' &&
      // @ts-expect-error - Telegram injects this proxy in its WebView
      (window.TelegramWebviewProxy !== undefined ||
        /Telegram/i.test(navigator.userAgent));
    if (!inTelegram) {
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-web-app.js';
    script.async = true;
    script.onload = () => {
      if (window.Telegram?.WebApp) {
        const tgWebApp = window.Telegram.WebApp;
        setWebApp(tgWebApp);
        tgWebApp.ready();
        tgWebApp.expand();
        setIsReady(true);

        // Apply Telegram theme
        if (tgWebApp.themeParams.bg_color) {
          document.documentElement.style.setProperty('--tg-theme-bg-color', tgWebApp.themeParams.bg_color);
        }
        if (tgWebApp.themeParams.text_color) {
          document.documentElement.style.setProperty('--tg-theme-text-color', tgWebApp.themeParams.text_color);
        }
      }
    };
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const isInTelegram = !!webApp;
  const isMiniApp = isInTelegram && !!webApp?.initDataUnsafe?.user;

  const contextValue: TelegramContextType = {
    webApp,
    user: webApp?.initDataUnsafe?.user || null,
    isInTelegram,
    isMiniApp,
    colorScheme: webApp?.colorScheme || 'light',
    themeParams: webApp?.themeParams || {},
    ready: () => {
      if (webApp && !isReady) {
        webApp.ready();
        setIsReady(true);
      }
    },
    expand: () => webApp?.expand(),
    close: () => webApp?.close(),
    showAlert: (message: string) => {
      return new Promise((resolve) => {
        if (webApp) {
          webApp.showAlert(message, resolve);
        } else {
          alert(message);
          resolve();
        }
      });
    },
    showConfirm: (message: string) => {
      return new Promise((resolve) => {
        if (webApp) {
          webApp.showConfirm(message, resolve);
        } else {
          resolve(confirm(message));
        }
      });
    },
    hapticFeedback: (type) => {
      // HapticFeedback requires version 6.1+
      if (webApp?.HapticFeedback && isVersionAtLeast(webApp.version, '6.1')) {
        if (type === 'success' || type === 'warning' || type === 'error') {
          webApp.HapticFeedback.notificationOccurred(type);
        } else {
          webApp.HapticFeedback.impactOccurred(type);
        }
      }
    },
    openLink: (url: string) => {
      if (webApp) {
        webApp.openLink(url);
      } else {
        window.open(url, '_blank');
      }
    },
    setMainButton: (text: string, onClick: () => void) => {
      if (webApp?.MainButton) {
        webApp.MainButton.setText(text);
        webApp.MainButton.onClick(onClick);
        webApp.MainButton.show();
      }
    },
    hideMainButton: () => {
      webApp?.MainButton?.hide();
    },
    showBackButton: (onClick: () => void) => {
      // BackButton requires version 6.1+
      if (webApp?.BackButton && isVersionAtLeast(webApp.version, '6.1')) {
        webApp.BackButton.onClick(onClick);
        webApp.BackButton.show();
      }
    },
    hideBackButton: () => {
      // BackButton requires version 6.1+
      if (webApp?.BackButton && isVersionAtLeast(webApp.version, '6.1')) {
        webApp.BackButton.hide();
      }
    },
  };

  return (
    <TelegramContext.Provider value={contextValue}>
      {children}
    </TelegramContext.Provider>
  );
}

export function useTelegram() {
  const context = useContext(TelegramContext);
  if (!context) {
    throw new Error('useTelegram must be used within a TelegramProvider');
  }
  return context;
}

export default TelegramContext;
